<template>
  <div class="overflow-hidden" v-resize="onResize">
    <v-tabs
      :background-color="$vuetify.theme.themes[theme].background"
      v-model="tab"
      style="z-index: 1"
      :height="TABS_HEIGHT"
    >
      <v-tab>
        {{ generateMessage('requests.details.titles.property') }}
      </v-tab>
      <v-tab>
        <v-badge color="error" v-if="!hasSeenInterventions">
          <span slot="badge">!</span>
          {{ generateMessage('requests.details.titles.interventions') }}
        </v-badge>
        <div v-else>
          {{ generateMessage('requests.details.titles.interventions') }}
        </div>
      </v-tab>
      <v-tab v-show="activeRecurrence || isRecurrence">
        <v-badge color="error" v-if="!hasSeenRecurrence">
          <span slot="badge">!</span>
          {{ generateMessage('requests.details.titles.recurrence') }}
        </v-badge>
        <div v-else>
          {{ generateMessage('requests.details.titles.recurrence') }}
        </div>
      </v-tab>
      <v-tab>{{ generateMessage('requests.details.titles.fri') }}</v-tab>
      <v-tab>{{ generateMessage('requests.details.titles.logbook') }}</v-tab>
      <v-tab>{{ generateMessage('requests.details.titles.documents') }}</v-tab>
      <v-tab>Techniciens</v-tab>
    </v-tabs>
    <v-tabs-items
      v-model="tab"
      :style="{
        height: height + 'px',
        background: $vuetify.theme.themes[theme].background,
      }"
      class="overflow-y-auto overflow-x-hidden"
    >
      <v-tab-item>
        <loader :is-loading="isLoadingCurrent">
          <property-alerts />
          <property-general />
          <property-dangerous-situations />
          <div class="d-flex flex-wrap">
            <v-col cols="12" md="6">
              <property-contract />
            </v-col>
            <v-col cols="12" md="6">
              <property-exploitation />
            </v-col>
          </div>
          <property-specific-symptoms />
        </loader>
      </v-tab-item>
      <v-tab-item>
        <property-interventions />
      </v-tab-item>
      <v-tab-item>
        <property-recurrence />
      </v-tab-item>
      <v-tab-item>
        <summary-property-sheet />
      </v-tab-item>
      <v-tab-item>
        <logbook-reduced-filters
          :style="{
            height: '80px',
          }"
        />
        <div
          :style="{
            height: height - 80 + 'px',
            'overflow-y': 'auto',
          }"
        >
          <logbook />
        </div>
      </v-tab-item>
      <v-tab-item>
        <documents-horizontal-filters
          :style="{
            height: '80px',
          }"
        />
        <div
          :style="{
            height: height - 80 + 'px',
            'overflow-y': 'auto',
          }"
        >
          <property-documents is-modal />
        </div>
      </v-tab-item>
      <v-tab-item>
        <custom-technician-selector-table
          :entity-id="request.entity_nav_full_id"
          :property-id="request.property_id"
          :request="request"
          :affectation="false"
        />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { generateMessage } from '@/utils/i18n';
import PropertyExploitation from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertyExploitation';
import PropertyContract from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertyContract';
import PropertyGeneral from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertyGeneral';
import PropertyDangerousSituations from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertyDangerousSituations';
import PropertyAlerts from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertyAlerts';
import PropertySpecificSymptoms from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertySpecificSymptoms';
import PropertyRecurrence from '@/pages/eDispatch/requestDetails/rightSide/recurrenceTab/PropertyRecurrence';
import PropertyInterventions from '@/pages/eDispatch/requestDetails/rightSide/historyTab/PropertyInterventions';
import PropertyDocuments from '@/pages/property/documents/PropertyDocuments';
import DocumentsHorizontalFilters from '@/pages/property/documents/DocumentsHorizontalFilters';
import Logbook from '@/pages/property/logbook/Logbook';
import LogbookReducedFilters from '@/pages/property/logbook/LogbookReducedFilters';
import SummaryPropertySheet from '@/pages/FRI/SummaryPropertySheet';
import CustomTechnicianSelectorTable from '@/components/selects/technician/CustomTechnicianSelectorTable';
import {
  HISTORY_FILTERS_HEIGHT,
  TABS_HEIGHT,
} from '@/pages/eDispatch/requestDetails/rightSide/requestsRightSide';
import {
  POPUP_BOTTOM_HEIGHT,
  POPUP_TOP_HEIGHT,
} from '@/components/dialogs/customDialog.styles';
import resizable from '@/business/mixins/resizable';
import Loader from '@/components/widgets/Loader';
import HISTORY_DATATABLE_FOOTER_HEIGHT from '@/pages/eDispatch/requestDetails/rightSide/historyTab/HistoryDataTable.styles';

function getInitialTab(name) {
  if (name === 'interventions') return 1;
  if (name === 'recurrence') return 2;
  return 0;
}

export default {
  name: 'RequestRightSide',
  mixins: [resizable],
  components: {
    Loader,
    PropertyExploitation,
    PropertyContract,
    PropertyGeneral,
    PropertyDangerousSituations,
    PropertyAlerts,
    PropertySpecificSymptoms,
    PropertyRecurrence,
    PropertyInterventions,
    SummaryPropertySheet,
    PropertyDocuments,
    DocumentsHorizontalFilters,
    Logbook,
    LogbookReducedFilters,
    CustomTechnicianSelectorTable,
  },
  computed: {
    ...mapGetters({
      activeRecurrence: 'requests/activeRecurrence',
      isLoadingCurrent: 'properties_360/isLoadingCurrent',
      isLoadingRecurrence: 'properties/requests/isLoadingItems',
      isRecurrence: 'properties/requests/isRecurrence',
      isLoadingInterventions: 'properties/interventions/isLoadingItems',
      interventionsData: 'properties/interventions/items',
      isLoadingPlannedInterventions:
        'properties/interventions/planned/isLoadingItems',
      plannedInterventionsData: 'properties/interventions/planned/items',
      request: 'requests/active',
    }),
    theme() {
      return this.$vuetify.theme.dark ? 'dark' : 'light';
    },
    height() {
      return (
        this.boilerplatePopupHeight -
        TABS_HEIGHT -
        POPUP_TOP_HEIGHT -
        POPUP_BOTTOM_HEIGHT
      );
    },
    modal() {
      return this.$store.getters['boilerplate/isModalOpen'];
    },
  },
  watch: {
    modal(value) {
      if (value) {
        this.tab = getInitialTab(
          this.$store.getters['boilerplate/modalInitialTab']
        );
      }
    },
    isLoadingRecurrence(value) {
      if (!value) {
        this.hasSeenRecurrence = this.tab === 2;
      }
    },
    isLoadingInterventions(value) {
      if (!value && this.interventionsData.length) {
        this.hasSeenInterventions = this.tab === 1;
      }
    },
    isLoadingPlannedInterventions(value) {
      if (!value && this.plannedInterventionsData.length) {
        this.hasSeenInterventions = this.tab === 1;
      }
    },
    tab(value) {
      if (value === 1) {
        this.hasSeenInterventions = true;
      }
      if (value === 2) {
        this.hasSeenRecurrence = true;
      }
    },
  },
  methods: {
    generateMessage,
  },
  data() {
    return {
      tab: getInitialTab(this.$store.getters['boilerplate/modalInitialTab']),
      hasSeenRecurrence: false,
      hasSeenInterventions: true,
      TABS_HEIGHT,
      HISTORY_DATATABLE_FOOTER_HEIGHT,
      HISTORY_FILTERS_HEIGHT,
    };
  },
};
</script>

<style scoped></style>
